import { Shield } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useCharacterSheet } from "@/hooks/use-character-sheet";
import type { PartyPc } from "@/lib/core/schemas/party-view";
import { RollLineRow } from "./roll-line-row";

const ABILITY_ORDER = ["str", "dex", "con", "int", "wis", "cha"] as const;

function formatSpeed(speed: Record<string, number>): string {
  const entries = Object.entries(speed);
  if (entries.length === 0) {
    return "—";
  }
  return entries
    .map(([mode, value]) => `${mode.replace("movement:", "")} ${value}ft`)
    .join(", ");
}

function signed(n: number): string {
  return n >= 0 ? `+${n}` : `${n}`;
}

export function SheetPanel({
  pc,
  campaignId,
}: {
  pc: PartyPc | null;
  campaignId: string;
}) {
  const { sheet, isLoading, error } = useCharacterSheet(
    campaignId,
    pc?.id ?? null
  );

  if (!pc) {
    return (
      <div className="py-8 text-center text-muted-foreground text-sm">
        No character selected.
      </div>
    );
  }
  if (error) {
    return (
      <div className="py-8 text-center text-muted-foreground text-sm">
        Couldn't load character sheet.
      </div>
    );
  }
  if (isLoading || !sheet) {
    return (
      <div className="py-8 text-center text-muted-foreground text-sm">
        Loading…
      </div>
    );
  }

  const classLine =
    sheet.classes.map((c) => `${c.className} ${c.level}`).join(" / ") ||
    sheet.creatureType ||
    "—";
  const defenseCount =
    sheet.damageResistances.length +
    sheet.damageImmunities.length +
    sheet.damageVulnerabilities.length +
    sheet.conditionImmunities.length;

  return (
    <div className="space-y-4 pb-16">
      <Card className="border-border bg-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-foreground text-xl">
            {sheet.name}
          </CardTitle>
          <span className="text-muted-foreground text-sm capitalize">
            {classLine}
          </span>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
            <span className="flex items-center gap-1">
              <Shield className="h-4 w-4 text-primary" /> AC <b>{sheet.ac}</b>
            </span>
            <span>
              HP{" "}
              <b>
                {sheet.hp.current}/{sheet.hp.max}
              </b>
            </span>
            <span>
              Init <b>{signed(sheet.initiativeBonus)}</b>
            </span>
            <span>
              PB <b>+{sheet.proficiencyBonus}</b>
            </span>
            <span>Speed: {formatSpeed(sheet.speeds)}</span>
          </div>
          <div className="grid grid-cols-6 gap-2">
            {ABILITY_ORDER.map((k) => {
              const a = sheet.abilities[k];
              return (
                <div
                  className="flex flex-col items-center rounded-lg bg-secondary/50 p-2"
                  key={k}
                >
                  <span className="text-muted-foreground text-xs uppercase">
                    {k}
                  </span>
                  <span className="font-bold text-foreground text-xl">
                    {a?.score ?? "—"}
                  </span>
                  <span className="text-primary text-xs">
                    {a ? signed(a.modifier) : ""}
                  </span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Accordion
        className="rounded-lg border border-border bg-card px-3"
        type="multiple"
      >
        <AccordionItem value="saves">
          <AccordionTrigger>Saves</AccordionTrigger>
          <AccordionContent>
            {ABILITY_ORDER.map((k) => (
              <RollLineRow
                key={k}
                label={`${k.toUpperCase()} save`}
                mods={sheet.saves[k] ?? []}
              />
            ))}
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="skills">
          <AccordionTrigger>Skills</AccordionTrigger>
          <AccordionContent>
            {Object.keys(sheet.skills)
              .sort()
              .map((skill) => (
                <RollLineRow
                  key={skill}
                  label={skill}
                  mods={sheet.skills[skill as keyof typeof sheet.skills] ?? []}
                />
              ))}
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="checks">
          <AccordionTrigger>Ability checks</AccordionTrigger>
          <AccordionContent>
            {ABILITY_ORDER.map((k) => (
              <RollLineRow
                key={k}
                label={`${k.toUpperCase()} check`}
                mods={sheet.abilityChecks[k] ?? []}
              />
            ))}
          </AccordionContent>
        </AccordionItem>
        {defenseCount > 0 && (
          <AccordionItem value="defenses">
            <AccordionTrigger>Defenses</AccordionTrigger>
            <AccordionContent className="space-y-1 text-sm">
              {sheet.damageResistances.length > 0 && (
                <div>Resist: {sheet.damageResistances.join(", ")}</div>
              )}
              {sheet.damageImmunities.length > 0 && (
                <div>Immune (dmg): {sheet.damageImmunities.join(", ")}</div>
              )}
              {sheet.damageVulnerabilities.length > 0 && (
                <div>Vuln: {sheet.damageVulnerabilities.join(", ")}</div>
              )}
              {sheet.conditionImmunities.length > 0 && (
                <div>Immune (cond): {sheet.conditionImmunities.join(", ")}</div>
              )}
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>
    </div>
  );
}
